import React, { useState, useEffect } from 'react';
import AccommodationSearchForm from './components/AccommodationSearchForm';
import AccommodationSearchResult from './components/AccommodationSearchResult';
import CheckboxFilter from './components/CheckboxFilter';
import RangeFilter from './components/RangeFilter';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import BeatLoader from 'react-spinners/BeatLoader';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { sendSkiInnEvent, ecommerceViewItemList } from '../../components/GA4';

const urlParams = new URLSearchParams(window.location.search);
const API_DATE_FORMAT = 'YYYY-MM-DD';

const ACCOMMODATIONS_ENDPOINT =
  drupalSettings.active_site === 'Ruka'
    ? process.env.RUKA_SKI_INN_SEARCH_ENDPOINT
    : process.env.PYHA_SKI_INN_SEARCH_ENDPOINT;

const AccomondationSearch = (props) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 920px)' });
  const [pets, setPets] = useState(urlParams.get('pets') == 1 ? true : false);
  const [startDate, setStartDate] = useState(
    urlParams.get('startDate')
      ? moment(urlParams.get('startDate'), API_DATE_FORMAT)
      : undefined
  );
  const [endDate, setEndDate] = useState(
    urlParams.get('endDate')
      ? moment(urlParams.get('endDate'), API_DATE_FORMAT)
      : undefined
  );
  const [adults, setAdults] = useState(parseInt(urlParams.get('adults')) || 2);
  const [children, setChildren] = useState(
    parseInt(urlParams.get('children')) || 0
  );
  const [infants, setInfants] = useState(
    parseInt(urlParams.get('infants')) || 0
  );
  const [activeSizeFilters, setActiveSizeFilters] = useState([]);
  const [activeThemeFilters, setActiveThemeFilters] = useState([]);
  const [activeFacilityFilters, setActiveFacilityFilters] = useState([]);
  const [activeDepartmentFilters, setActiveDepartmentFilters] = useState([]);
  const [allDepartmentsActive, setAllDepartmentsActive] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(
    isTabletOrMobile ? false : true
  );
  const [liftDistanceFilter, setLiftDistanceFilter] = useState();
  const [skiTrailDistanceFilter, setSkiTrailDistanceFilter] = useState();
  const [villageDistanceFilter, setVillageDistanceFilter] = useState();
  const [sortType, setSortType] = useState('field_area_m2');
  const [sortOrder, setSortOrder] = useState('asc');
  const [products, setProducts] = useState({
    data: null,
    success: false,
  });
  const [filters, setFilters] = useState();
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [ownershipData, setOwnershipData] = useState();
  const departmentName = drupalSettings.departmentName || urlParams.get('department');
  const [activeUrlParams, setActiveUrlParams] = useState(urlParams.toString());
  const searchDescription = drupalSettings.searchDescription ?? '';
  const searchResultsWrapperRef = React.useRef(null);
  const [email, setEmail] = useState();
  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);
  const [isPetOwner, setIsPetOwner] = useState();
  const [numberOfSearches, setNumberOfSearches] = useState(1);
  const [selectedPopularDate, setSelectedPopularDate] = useState();
  const [uniqueDepartments, setUniqueDepartments] = useState([]);
  const [isCompressedList, setIsCompressedList] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [filteringComplete, setFilteringComplete] = useState(false);

  const dataLayer = window.dataLayer;

  function sortResults(type, order) {
    if (type == 'field_title') {
      return (a, b) =>
        a[type][drupalSettings.path.currentLanguage] >
        b[type][drupalSettings.path.currentLanguage]
          ? 1
          : -1;
    } else if (type == 'price') {
      if (order == 'desc') {
        return (a, b) => (parseInt(a[type]) < parseInt(b[type]) ? 1 : -1);
      } else {
        return (a, b) => (parseInt(a[type]) > parseInt(b[type]) ? 1 : -1);
      }
    } else {
      return (a, b) => {
        // If compared values are equal return 0
        // to keep list items from flipping around
        if (a[type] === b[type]) {
          return 0;
        } 
        if (order === 'desc') {
          return a[type] < b[type] ? 1 : -1;
        } else {
          return a[type] > b[type] ? 1 : -1;
        }
      };
    }
  }

  function sortByOwnership(a, b) {
    if (ownershipData) {
      if (
        !ownershipData.some(
          (room) => room.field_product_id == a.field_product_id
        ) &&
        ownershipData.some(
          (room) => room.field_product_id == b.field_product_id
        )
      ) {
        return 1;
      }
      if (
        ownershipData.some(
          (room) => room.field_product_id == a.field_product_id
        ) &&
        !ownershipData.some(
          (room) => room.field_product_id == b.field_product_id
        )
      ) {
        return -1;
      }
    }
  }

  function filterSizes({ field_bedrooms_list }) {
    if (!activeSizeFilters.length) {
      return true;
    } else {
      return activeSizeFilters.some((value) =>
        field_bedrooms_list.includes(value)
      );
    }
  }

  function filterThemes({ field_theme }) {
    if (!activeThemeFilters.length) {
      return true;
    } else {
      return activeThemeFilters.every((value) => field_theme.includes(value));
    }
  }

  function filterFacilities({ field_facilities }) {
    if (!activeFacilityFilters.length) {
      return true;
    } else {
      return activeFacilityFilters.every((value) =>
        field_facilities.includes(value)
      );
    }
  }

  function filterLiftDistance({ field_distance_lift }) {
    return field_distance_lift <= liftDistanceFilter;
  }

  function filterSkiTrailDistance({ field_distance_skitrail }) {
    return field_distance_skitrail <= skiTrailDistanceFilter;
  }

  function filterVillageDistance(result) {
    if (result.field_distance_to_pyha_village) {
      return result.field_distance_to_pyha_village <= villageDistanceFilter;
    }
    return result.field_distance_village <= villageDistanceFilter;
  }

  const filterDuplicateResults = (accumulator, currentValue) => {
    let hasDuplicate = accumulator.find(
      (item) => item.field_product_id === currentValue.field_product_id
    );
    if (!hasDuplicate) {
      return accumulator.concat([currentValue]);
    } else {
      return accumulator;
    }
  };

  function filterDepartment(result) {
    if (activeDepartmentFilters.length > 0) {
      let departmentActive = false;
      activeDepartmentFilters.map((department) => {
        if (department.fi === result.field_capacity.name.fi) {
          departmentActive = true;
        }
      });
      return departmentActive;
    }
    else if (departmentName) {
      return result.departmentName === departmentName;
    }
    else {
      return false;
    }
  }

  function filterMissingNoPetsRoomTypes({ variations }) {
    if (variations.length === 1 && variations[0].field_pets_allowed) {
      return false;
    } else {
      return true;
    }
  }

  function filterResults(results) {
    if (!results) return [];

    // Remove duplicates
    let res = results.reduce(filterDuplicateResults, []);

    if (props.results === 'campaign') {
      res = res.filter(props.customFilter);
    } else {
      // Go through products array only once,
      // and apply all the filters.
      res = res.filter(item => 
        filterFacilities(item) &&
        filterSizes(item) &&
        filterThemes(item) &&
        filterLiftDistance(item) &&
        filterVillageDistance(item) &&
        filterSkiTrailDistance(item) &&
        filterMissingNoPetsRoomTypes(item) &&
        filterDepartment(item)
      );
    }

    return res;
  }

  function handleShowFilters() {
    if (filtersOpen) {
      setFiltersOpen(false);
    } else {
      setFiltersOpen(true);
    }
  }

  function countResults() {
    if(!filteringComplete) {
      return null;
    }

    let numberOfResults = filteredResults.length;
    if (numberOfResults == 0) {
      return Drupal.t('No results');
    } else if (numberOfResults == 1) {
      return Drupal.t('@number Ski-Inn accommodation', {
        '@number': `${numberOfResults}`,
      });
    } else {
      return Drupal.t('@number Ski-Inn accommodations', {
        '@number': `${numberOfResults}`,
      });
    }
  }

  function formatPrice(price) {
    return Number.parseFloat(price).toFixed(0);
  }

  async function fetchApi(e) {
    if (e) {
      e.preventDefault();

      searchResultsWrapperRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    const API_URL = `${process.env.IPS_API_URL}/api/${ACCOMMODATIONS_ENDPOINT}?resort=${drupalSettings.active_site}&`;

    setLoadingProducts(true);
    setProducts({
      data: null,
      success: false,
    });

    try {
      const response = await fetch(`${API_URL}${activeUrlParams}`, {
        method: 'GET',
        headers: {
          token: Cookies.get('mo.id.token.Azure.AD.B2C'),
          email,
        },
      })

      if(!response.ok) {
        setLoadingProducts(false);
        setNoResults(true);
        return;
      }

      const searchData = await response.json();

      const results = searchData.result
        .sort((a, b) => a.price - b.price) // Display lowest available price for the accommodation.
        .filter((item) => {
          if (
            !drupalSettings.drupal_product_ids.includes(
              item.field_product_id
            )
          ) {
            return false;
          }

          if (item.resort !== drupalSettings.active_site) {
            console.log(
              'Wrong resort in the search results',
              `${API_URL}${activeUrlParams}`,
              searchData
            );

            return false;
          }

          return true;
        });

      // Set these variables here, and update them accordingly if user is an owner.
      let ownedApartments = [];
      let combinedResults = results;

      if (searchData.ownershipData && searchData.ownershipData.rooms) {
        // Check which products the user owns.
        const ownedRoomTypeIds = searchData.ownershipData.rooms.map((room) =>
          parseInt(room.roomTypeId)
        );

        // Build a set of roomTypeIds, fill it with ids already present in results
        const resultRoomTypeIds = new Set();

        results.forEach((result) => {
          result.variations.forEach((variation) => {
            resultRoomTypeIds.add(parseInt(variation.field_hotellinx_id));
          });
        });

        // Check if owned products are already found in results
        const ownedApartmentsInResults = results.filter((result) =>
          result.variations.some((variation) =>
            ownedRoomTypeIds.includes(
              parseInt(variation.field_hotellinx_id)
            )
          )
        );

        // If owned apartments are found in results -> add them to owned apartments
        if (ownedApartmentsInResults.length > 0) {
          ownedApartments.push(...ownedApartmentsInResults);
        }

        // Find ownedRoomTypeIds that are not present in results
        const missingOwnedRoomTypeIds = ownedRoomTypeIds.filter(
          (id) => !resultRoomTypeIds.has(id)
        );

        // If there are missing roomTypeIds from results, call IPS again to fetch them
        if (missingOwnedRoomTypeIds.length > 0) {
          // Include missing roomtypeids in query
          const ownedRoomTypeIdsParam = missingOwnedRoomTypeIds.join(',');
          const ownedURL = `${API_URL}roomTypeIds=${ownedRoomTypeIdsParam}`;

          const ownedResponse = await fetch(ownedURL, {
            method: 'GET',
            headers: { 
              token: Cookies.get('mo.id.token.Azure.AD.B2C'),
              email,
            },
          });

          const ownedApartmentsData = await ownedResponse.json();
          const ownedApartmentsResult = ownedApartmentsData.result;

          // Include fetched owner apartments in products
          ownedApartments.push(...ownedApartmentsResult);
        }

        // Set ownership data and combine owned apartments with results
        setOwnershipData(ownedApartments);
        combinedResults = [...ownedApartments, ...results];

        // Check if the user owns exclusively pet rooms.
        // If they own both types, we don't change anything.
        if (isInitialPageLoad && !pets) {
          let ownerHasOnlyPetVariation = true;
          ownedApartments.forEach((product) => {
            if (!ownerHasOnlyPetVariation) {
              return;
            }
            product.variations.forEach((variation) => {
              if (
                ownedRoomTypeIds.includes(
                  parseInt(variation.field_hotellinx_id)
                )
              ) {
                if (!variation.field_pets_allowed) {
                  ownerHasOnlyPetVariation = false;
                }
              }
            });
          });
          setIsInitialPageLoad(false);

          // Pets checkbox gets checked, and the results are fetched again.
          if (ownerHasOnlyPetVariation) {
            setPets(true);
            updateQueryParams();
            setIsPetOwner(true);

            return;
          }
        } else if (isInitialPageLoad && pets) {
          setIsInitialPageLoad(false);
        }
      }
      
      setProducts({
        data: combinedResults,
        success: searchData.success,
      });
      ecommerceViewItemList(dataLayer, true, filterResults(combinedResults));
      setLoadingProducts(false);
      setNoResults(false);
    }
    catch(error) {
      setLoadingProducts(false);
      setNoResults(true);
    }
  }

  function updateFilterUrlParams(categoryName, activeFilters, fieldName) {
    if (filters) {
      filters[fieldName].some((filter) => {
        if (activeFilters.includes(filter.machine_name)) {
          urlParams.set(`${categoryName}_${filter.machine_name}`, '1');
        } else {
          urlParams.delete(`${categoryName}_${filter.machine_name}`);
        }
      });

      window.history.replaceState({}, '', `${location.pathname}?${urlParams}`);
    }
  }

  function updateQueryParams() {
    if (startDate && endDate) {
      urlParams.set('startDate', startDate.format(API_DATE_FORMAT));
      urlParams.set('endDate', endDate.format(API_DATE_FORMAT));
    } else {
      urlParams.delete('startDate');
      urlParams.delete('endDate');
    }

    if (pets) {
      urlParams.set('pets', 1);
    } else {
      urlParams.delete('pets');
    }

    urlParams.set('adults', adults);
    urlParams.set('children', children);
    urlParams.set('infants', infants);

    setActiveUrlParams(urlParams.toString());
    window.history.replaceState(
      {},
      '',
      `${location.pathname}?${activeUrlParams}`
    );
  }

  function sendToGA4(results) {
    if (results) {
      let eventName;
      if (numberOfSearches === 1) {
        eventName = 'ski_inn_primary_search';
      } else {
        eventName = 'ski_inn_refine_search';
      }
      const activeDepartmentsEng = [];
      activeDepartmentFilters.map((department) => {
        activeDepartmentsEng.push(department.en);
      });
      setNumberOfSearches(() => numberOfSearches + 1);
      sendSkiInnEvent(
        dataLayer,
        eventName,
        startDate,
        endDate,
        adults,
        children,
        infants,
        pets,
        `${sortType}.${sortOrder}`,
        activeThemeFilters,
        activeSizeFilters,
        activeFacilityFilters,
        activeDepartmentsEng,
        liftDistanceFilter === 500 ? null : liftDistanceFilter,
        skiTrailDistanceFilter === 500 ? null : skiTrailDistanceFilter,
        villageDistanceFilter === 2000 ? null : villageDistanceFilter,
        filterResults(results).length,
        selectedPopularDate
      );
    }
  }

  useEffect(() => {
    updateFilterUrlParams('theme', activeThemeFilters, 'field_theme');
  }, [activeThemeFilters]);

  useEffect(() => {
    updateFilterUrlParams('size', activeSizeFilters, 'field_bedrooms_list');
  }, [activeSizeFilters]);

  useEffect(() => {
    updateFilterUrlParams(
      'facility',
      activeFacilityFilters,
      'field_facilities'
    );
  }, [activeFacilityFilters]);

  useEffect(() => {
    // If we have all departments marked as active but the filters don't include all unique ones
    // we set it to false and update checkbox.
    if (allDepartmentsActive && activeDepartmentFilters.length < uniqueDepartments.length) {
      setAllDepartmentsActive(false);
    }

    // If we are on a specifc department page and the user has unselected all values we re-select that option.
    if (activeDepartmentFilters.length === 0 && departmentName && uniqueDepartments.length > 0) {
      if (products.data) {
        const firstMatchingResult = products.data.find(department => department.departmentName === departmentName);
        setActiveDepartmentFilters([firstMatchingResult.field_capacity.name]);
      }
    }

  }, [activeDepartmentFilters]);

  useEffect(() => {
    if (allDepartmentsActive) {
      setActiveDepartmentFilters(uniqueDepartments);
    }
  }, [allDepartmentsActive]);

  useEffect(() => {
    async function getEmail() {
      const userInfoResponse = await fetch(
        `${drupalSettings.base_url}/getUserInfo`
      );
      // Anonymous users just get a 403 error.
      if (userInfoResponse.ok) {
        const userInfo = await userInfoResponse.json();
        setEmail(userInfo[0].mail);
      } else {
        setEmail('empty');
      }
    }
    getEmail();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (email) {
        await fetchApi();
      }
    };
  
    fetchData();
  }, [setProducts, email, isPetOwner]);

  useEffect(() => {
    updateQueryParams();
  }, [fetchApi]);

  useEffect(() => {
    async function getFilters() {
      const API_URL = `${process.env.IPS_API_URL}/api/filters?resort=${drupalSettings.active_site}`;
      const response = await fetch(API_URL);
      if (response.ok) {
        const { filters } = await response.json();
        filters.field_facilities.sort((a, b) => a.weight - b.weight);
        filters.field_bedrooms_list.sort((a, b) => a.weight - b.weight);
        filters.field_theme.sort((a, b) => a.weight - b.weight);
        setFilters(filters);
      }
    }
    getFilters();
  }, []);

  useEffect(() => {
    if (!loadingProducts) {
      setFilteringComplete(false);
      // Delay the function call because otherwise
      // every slider increment change would trigger a call
      const delayDebounceFn = setTimeout(() => {
        if (!products.data) {
          sendToGA4([]);
          setFilteredResults([]);
          setFilteringComplete(true);
        } else {
          const filtered = filterResults(products.data);
          setFilteredResults(filtered);
          sendToGA4(filtered);
          setFilteringComplete(true);

          if (uniqueDepartments.length === 0) {
            const departments = [];
            let activeDepartments = [];
            let specificDepartment = false;
            products.data.map((result) => {
              if (result && result.field_capacity) {
                const name = result.field_capacity.name;
                if (!departments.some((department) => department.fi === name.fi)) {
                  departments.push(name);
                }
                if (departmentName && activeDepartments.length === 0) {
                  if (departmentName === result.departmentName) {
                    activeDepartments.push(name);
                  }
                }
              }
            });
            setUniqueDepartments(departments);
            if (activeDepartmentFilters.length === 0 && !specificDepartment) {
              if (!departmentName)  {
                activeDepartments = activeDepartments.concat(departments);
                setAllDepartmentsActive(true);
              }
              setActiveDepartmentFilters(activeDepartments);
            }
          }
        }
      }, 1000);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [
    activeDepartmentFilters,
    activeFacilityFilters,
    activeSizeFilters,
    activeThemeFilters,
    loadingProducts,
    liftDistanceFilter,
    skiTrailDistanceFilter,
    villageDistanceFilter,
  ]);

  return (
    <div className="accommodation-search">
      {props.results !== 'campaign' && (
        <AccommodationSearchForm
          setPets={setPets}
          pets={pets}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          adults={adults}
          setAdults={setAdults}
          kids={children}
          setChildren={setChildren}
          infants={infants}
          setInfants={setInfants}
          buttonText={Drupal.t('Search for accommodation')}
          onSubmit={fetchApi}
          availabilityFound={true}
          setSelectedPopularDate={setSelectedPopularDate}
        />
      )}
      <div className="accommodation-search__description">
        {searchDescription}
      </div>
      <div
        className={`accommodation-search__content-wrapper ${props.results}`}
        ref={searchResultsWrapperRef}
        style={noResults ? { gridTemplateColumns: '100%' } : {}}
      >
        <div className="accommodation-search__results_wrapper">
          {!noResults && (
            <div className="accommodation-search__results_header_wrapper">
              <div className="accommodation-search__results_count">
                {products.data && !loadingProducts ? countResults() : null}
              </div>
              <div className="accommodation-search__results_sort">
                <label>
                  {Drupal.t('Sort by', {}, { context: 'search' })}:{' '}
                </label>
                <div className="form-item__dropdown form-item__dropdown--inline">
                  <select
                    onChange={(e) => {
                      setSortType(e.target.value);
                      setSortOrder(
                        e.target.options[e.target.selectedIndex].dataset.order
                      );
                    }}
                    className="form-item__select form-item__select--inline"
                  >
                    <option value="field_area_m2" data-order="asc">
                      {Drupal.t('Size, ascending')}
                    </option>
                    <option value="field_area_m2" data-order="desc">
                      {Drupal.t('Size, descending')}
                    </option>
                    <option value="field_title">{Drupal.t('Name')}</option>
                    {products.data &&
                    products.data.length > 0 &&
                    products.data[0].price ? (
                      <>
                        <option value="price" data-order="asc">
                          {Drupal.t('Price, ascending')}
                        </option>
                        <option value="price" data-order="desc">
                          {Drupal.t('Price, descending')}
                        </option>
                      </>
                    ) : null}
                  </select>
                </div>
              </div>
              <div className="accommodation-search__results_list_type">
                <label className='visually-hidden'>
                  {Drupal.t('List type', {}, { context: 'search' })}:{' '}
                </label>
                <div className='form-item--radio form-item--radio__item'>
                  <input
                    className="form-item--radio__input visually-hidden"
                    type="radio"
                    name="type"
                    id="full"
                    value="full"
                    defaultChecked
                    onChange={() => {(setIsCompressedList(false))}}
                  />
                  <label htmlFor="full" className="form-item__label form-item__label--radio">
                    <span className='visually-hidden'>{Drupal.t('Show more information for a result', {}, { context: 'search' })}</span>
                  </label>
                </div>
                <div className="form-item--radio form-item--radio__item">
                  <input 
                    className="form-item--radio__input visually-hidden"
                    type="radio"
                    name="type"
                    id="compressed"
                    value="compressed"
                    onChange={() => {(setIsCompressedList(true))}}
                  />
                  <label htmlFor="compressed" className="form-item__label form-item__label--radio">
                    <span className='visually-hidden'>{Drupal.t('Show less information for a result', {}, { context: 'search' })}</span>
                  </label>
                </div>
              </div>
            </div>
          )}
          {!loadingProducts ? (
            products.data ? (
              filteringComplete && filteredResults
                .sort(sortResults(sortType, sortOrder))
                .sort(sortByOwnership)
                .map((result, index) => (
                  isCompressedList ? (
                    <div key={index} className="accommodation-search__results-compressed">
                      <AccommodationSearchResult 
                        id={result.roomTypeId}
                        product_id={result.field_product_id}
                        beds={result.field_beds}
                        price={result.price ? formatPrice(result.price) : null}
                        title={
                          result.field_title
                          ? result.field_title[
                            drupalSettings.path.currentLanguage
                            ]
                            : ''
                        }
                        productParams={urlParams.toString()}
                        hasOwnership={
                          ownershipData
                            ? ownershipData.some(
                                (room) =>
                                  room.field_product_id == result.field_product_id
                              )
                            : false
                        }
                      />
                    </div>
                      ) : (
                  <div key={index}>
                      <AccommodationSearchResult
                        id={result.roomTypeId}
                        product_id={result.field_product_id}
                        title={
                          result.field_title
                            ? result.field_title[
                              drupalSettings.path.currentLanguage
                              ]
                            : ''
                        }
                        description={result.field_short_description
                            ? result.field_short_description[
                                drupalSettings.path.currentLanguage
                              ]
                            : ''
                        }
                        images={result.field_images}
                        area={result.field_area_m2}
                        beds={result.field_beds}
                        village_distance={
                          result.field_distance_village ||
                          result.field_distance_village == 0
                            ? result.field_distance_village // Distance to Ruka.
                            : result.field_distance_to_pyha_village
                       }
                        lift_distance={result.field_distance_lift}
                        skitrail_distance={result.field_distance_skitrail}
                        price={result.price ? formatPrice(result.price) : null}
                        productParams={urlParams.toString()}
                        hasOwnership={
                          ownershipData
                            ? ownershipData.some(
                                (room) =>
                                  room.field_product_id == result.field_product_id
                              )
                            : false
                        }
                        departmentName={result.field_capacity.name}
                        housingType={result.field_housing_type_list[0]}
                        roomName={result.roomName}
                      />
                  </div>
                )
                ))
            ) : (
              filteringComplete && 
                <div className="accommodation-search__no-results">
                  <h3>{Drupal.t('No results')}</h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: drupalSettings.search_no_results_message,
                    }}
                  />
                </div>
            )
          ) : (
            <div className="accommodation-search__spinner">
              <BeatLoader color="#ff1706" />
            </div>
          )}
        </div>
        {!noResults && (
          <>
            {isTabletOrMobile && props.results !== 'campaign' ? (
              <button
                onClick={handleShowFilters}
                className="button button--gray"
              >
                {!filtersOpen
                  ? Drupal.t('Show filtering options')
                  : Drupal.t('Hide filtering options')}
              </button>
            ) : null}

            {props.results !== 'campaign' && (
              <div
                className={`accommodation-search__filter-wrapper ${
                  !filtersOpen ? 'visually-hidden' : ''
                }`}
              >
                <div className="accommodation-search__filter-inner-wrapper">
                  <h3>{Drupal.t('Search conditions')}</h3>
                  <h4>{Drupal.t('Accommodation type')}</h4>
                  <CheckboxFilter
                        checked={allDepartmentsActive}
                        key={'index'}
                        name={Drupal.t('All')}
                        value={'All'}
                        setState={setAllDepartmentsActive}
                      />
                    {uniqueDepartments ? uniqueDepartments.map((filter, index) => (
                        <CheckboxFilter
                          checked={activeDepartmentFilters.some((department) => {return department.fi === filter.fi})}
                          key={index}
                          name={filter[drupalSettings.path.currentLanguage]}
                          value={filter}
                          setState={setActiveDepartmentFilters}
                        />
                      ))
                    : null}
                  <h4>{Drupal.t('Facilities')}</h4>
                  {filters
                    ? filters.field_facilities.map((filter, index) => (
                        <>
                          {filter.used_as_filter && (
                            <CheckboxFilter
                              checked={urlParams.has(
                                `facility_${filter.machine_name}`
                              )}
                              key={index}
                              setState={setActiveFacilityFilters}
                              name={
                                filter.name[drupalSettings.path.currentLanguage]
                              }
                              value={filter.machine_name}
                            />
                          )}
                        </>
                      ))
                    : null}

                  <h4>{Drupal.t('Size')}</h4>

                  {filters
                    ? filters.field_bedrooms_list.map((filter, index) => (
                        <CheckboxFilter
                          checked={urlParams.has(`size_${filter.machine_name}`)}
                          key={index}
                          name={
                            filter.name[drupalSettings.path.currentLanguage]
                          }
                          value={filter.machine_name}
                          setState={setActiveSizeFilters}
                        />
                      ))
                    : null}

                  <h4>{Drupal.t('Distances')}</h4>

                  <div className="accommodation-search__distance-filter-wrapper">
                    <RangeFilter
                      name="lift"
                      label={Drupal.t('To lift')}
                      min={0}
                      max={500}
                      step={50}
                      setState={setLiftDistanceFilter}
                    />
                    <RangeFilter
                      name="skitrail"
                      label={Drupal.t('To ski trail')}
                      min={0}
                      max={500}
                      step={50}
                      setState={setSkiTrailDistanceFilter}
                    />
                    <RangeFilter
                      name="village"
                      label={
                        drupalSettings.active_site == 'Ruka'
                          ? Drupal.t('To Ruka village')
                          : Drupal.t('To Pyhä village')
                      }
                      min={0}
                      max={2000}
                      step={100}
                      setState={setVillageDistanceFilter}
                    />
                  </div>

                  <h4>{Drupal.t('Accommodation theme')}</h4>

                  {filters
                    ? filters.field_theme.map((filter, index) => (
                        <CheckboxFilter
                          checked={urlParams.has(
                            `theme_${filter.machine_name}`
                          )}
                          key={index}
                          name={
                            filter.name[drupalSettings.path.currentLanguage]
                          }
                          value={filter.machine_name}
                          setState={setActiveThemeFilters}
                        />
                      ))
                    : null}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

AccomondationSearch.propTypes = {
  results: PropTypes.string,
  customFilter: PropTypes.func,
};

export default AccomondationSearch;
